// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pictures and files of an unsent agent prompt, kept with its text draft (AgentDrafts).
// Too big for the settings snapshot, so they live as blobs in IndexedDB on this device, one entry
// per draft key (a thread, or the new chat), and come back when the composer opens again.

import {getProtectedIndexedDB} from '@app/features/platform/state/ProtectedWebStorage';

const DB_NAME = 'konvo-agent-drafts';
const STORE = 'files';

interface StoredFile {
	name: string;
	type: string;
	lastModified: number;
	blob: Blob;
}

function openDatabase(): Promise<IDBDatabase | null> {
	const factory = getProtectedIndexedDB();
	if (!factory) return Promise.resolve(null);
	return new Promise((resolve) => {
		try {
			const request = factory.open(DB_NAME, 1);
			request.onupgradeneeded = () => {
				if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
			};
			request.onsuccess = () => resolve(request.result);
			request.onerror = () => resolve(null);
			request.onblocked = () => resolve(null);
		} catch {
			resolve(null);
		}
	});
}

// One read or write at a time, in the order asked, so a load that follows a delete never gets
// the deleted files back.
let queue: Promise<unknown> = Promise.resolve();

function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
	const next = queue.then(() => runInStore(mode, run));
	queue = next;
	return next;
}

async function runInStore<T>(
	mode: IDBTransactionMode,
	run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
	const db = await openDatabase();
	if (!db) return null;
	return new Promise((resolve) => {
		try {
			const tx = db.transaction(STORE, mode);
			const request = run(tx.objectStore(STORE));
			request.onsuccess = () => resolve(request.result ?? null);
			request.onerror = () => resolve(null);
			tx.oncomplete = () => db.close();
			tx.onabort = () => {
				db.close();
				resolve(null);
			};
		} catch {
			db.close();
			resolve(null);
		}
	});
}

/** The files kept for a draft, as Files again; empty when there are none (or no IndexedDB). */
export async function loadDraftFiles(key: string): Promise<Array<File>> {
	const stored = await withStore<Array<StoredFile> | undefined>('readonly', (store) => store.get(key));
	if (!Array.isArray(stored)) return [];
	return stored.map(
		(entry) => new File([entry.blob], entry.name, {type: entry.type, lastModified: entry.lastModified}),
	);
}

/** Keep these files for the draft; none forgets them. */
export async function saveDraftFiles(key: string, files: ReadonlyArray<File>): Promise<void> {
	if (files.length === 0) {
		await withStore('readwrite', (store) => store.delete(key));
		return;
	}
	const stored: Array<StoredFile> = files.map((file) => ({
		name: file.name,
		type: file.type,
		lastModified: file.lastModified,
		blob: file,
	}));
	await withStore('readwrite', (store) => store.put(stored, key));
}
